import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { 
  ArrowLeft, User, MapPin, Plus, Check, Home, FileText, Sparkles,
  Calculator, ChevronRight, Search, X, Loader, DollarSign, Clock,
  Repeat, Building2, Trash2, AlertCircle
} from 'lucide-react';
import Modal from '../components/Modal';
import { formatFullName, formatCurrency } from '../utils/helpers';
import { cleaningFrequencies, homeConditions } from '../utils/estimateData';
import { generateEstimate, ADDONS } from '../utils/pricingCalculator';
import './EstimateNew.css';

// Convert ADDONS object to array for deep clean add-ons (filter to relevant ones with prices)
const deepCleanAddonsList = Object.entries(ADDONS)
  .filter(([id, addon]) => addon.price !== null && !addon.requiresReview)
  .map(([id, addon]) => ({
    id,
    label: addon.label,
    price: addon.price,
    unit: addon.unit,
    category: addon.category
  }));

// Condition options with emojis - matching pricingCalculator.js BASE_RATES
const conditionOptions = [
  { value: 'super_clean', emoji: '✨', label: 'Super Clean', description: 'Immaculate' },
  { value: 'second_home', emoji: '🏡', label: 'Second Home', description: 'Rarely used' },
  { value: 'lived_in', emoji: '🏠', label: 'Lived In', description: 'Normal use' },
  { value: 'busy_home', emoji: '👨‍👩‍👧‍👦', label: 'Busy Home', description: 'Lots of activity' },
  { value: 'overwhelmed', emoji: '🚨', label: 'Overwhelmed', description: 'Needs extra care' }
];

// Available frequencies for recurring
const frequencyOptions = [
  { value: 'weekly', label: 'Weekly', multiplier: 1.0 },
  { value: 'biweekly', label: 'Bi-Weekly', multiplier: 1.15 },
  { value: 'monthly', label: 'Monthly', multiplier: 1.3 },
  { value: 'one_time', label: 'One-Time', multiplier: 1.5 }
];

function EstimateNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state, getClientById, getClientProperties, createEstimate, addClient, addProperty } = useApp();
  const { clients, properties } = state;

  // Pre-selected from URL
  const preSelectedClientId = searchParams.get('clientId');
  const preSelectedPropertyId = searchParams.get('propertyId');

  // Estimate type: 'residential' or 'custom'
  const [estimateType, setEstimateType] = useState('residential');

  // Client & Property selection
  const [selectedClientId, setSelectedClientId] = useState(preSelectedClientId || '');
  const [selectedPropertyId, setSelectedPropertyId] = useState(preSelectedPropertyId || '');
  const [clientSearch, setClientSearch] = useState('');
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [showNewPropertyModal, setShowNewPropertyModal] = useState(false);

  // New client form
  const [newClient, setNewClient] = useState({
    firstName: '', lastName: '', email: '', phone: ''
  });

  // New property form
  const [newProperty, setNewProperty] = useState({
    addressLine1: '', addressLine2: '', city: 'Charlottesville', state: 'VA', zip: '',
    sqft: '', bedrooms: '', bathrooms: ''
  });

  // Residential calculator inputs
  const [calcInputs, setCalcInputs] = useState({
    sqft: '',
    bedrooms: '',
    bathrooms: '',
    condition: 'lived_in',
    hasPets: false,
    petCount: 1
  });

  // Selected frequencies (can select multiple to show options)
  const [selectedFrequencies, setSelectedFrequencies] = useState(['biweekly']);

  // Deep clean add-ons
  const [selectedAddons, setSelectedAddons] = useState([]);

  // Skip deep clean option
  const [skipDeepClean, setSkipDeepClean] = useState(false);

  // Custom quote line items
  const [lineItems, setLineItems] = useState([
    { id: 1, description: '', qty: 1, unitPrice: 0, optional: false }
  ]);

  // Discount
  const [discount, setDiscount] = useState({ type: 'percent', value: 0, reason: '' });

  // Notes
  const [clientNotes, setClientNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');

  // UI State
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('client'); // for notes

  // Get selected client and their properties
  const selectedClient = selectedClientId ? getClientById(selectedClientId) : null;
  const clientProps = selectedClientId ? getClientProperties(selectedClientId) : [];
  const selectedProperty = selectedPropertyId ? properties.find(p => p.id === selectedPropertyId) : null;

  // Auto-fill property data into calculator
  useEffect(() => {
    if (selectedProperty) {
      setCalcInputs(prev => ({
        ...prev,
        sqft: selectedProperty.sqft || prev.sqft,
        bedrooms: selectedProperty.bedrooms || prev.bedrooms,
        bathrooms: selectedProperty.bathrooms || prev.bathrooms
      }));
    }
  }, [selectedProperty]);

  // Filter clients for search
  const filteredClients = useMemo(() => {
    if (!clientSearch) return clients.slice(0, 10);
    const search = clientSearch.toLowerCase();
    return clients.filter(c => {
      const name = `${c.firstName} ${c.lastName}`.toLowerCase();
      const email = (c.email || '').toLowerCase();
      const phone = (c.phone || '').replace(/\D/g, '');
      const searchDigits = clientSearch.replace(/\D/g, '');
      return name.includes(search) || email.includes(search) || 
             (searchDigits && phone.includes(searchDigits));
    }).slice(0, 10);
  }, [clients, clientSearch]);

  // Calculate estimates for all selected frequencies
  const estimates = useMemo(() => {
    if (estimateType !== 'residential') return {};
    if (!calcInputs.sqft || !calcInputs.bedrooms || !calcInputs.bathrooms) return {};

    const results = {};
    selectedFrequencies.forEach(freq => {
      // Map inputs to generateEstimate's expected structure
      const baseParams = {
        sqft: parseInt(calcInputs.sqft),
        condition: calcInputs.condition,
        frequency: freq,
        bedrooms: parseInt(calcInputs.bedrooms),
        fullBaths: Math.floor(parseFloat(calcInputs.bathrooms)),
        halfBaths: parseFloat(calcInputs.bathrooms) % 1 >= 0.5 ? 1 : 0,
        people: 3, // Default
        sheddingPets: calcInputs.hasPets ? calcInputs.petCount : 0,
        glassShowerDoors: 0
      };
      
      try {
        results[freq] = generateEstimate(baseParams, []);
      } catch (e) {
        console.error('Calc error:', e);
      }
    });
    return results;
  }, [estimateType, calcInputs, selectedFrequencies]);

  // Calculate add-ons total
  const addonsTotal = useMemo(() => {
    return selectedAddons.reduce((sum, addonId) => {
      const addon = deepCleanAddonsList.find(a => a.id === addonId);
      return sum + (addon?.price || 0);
    }, 0);
  }, [selectedAddons]);

  // Get primary estimate (first selected frequency)
  const primaryFreq = selectedFrequencies[0] || 'biweekly';
  const primaryEstimate = estimates[primaryFreq];

  // Calculate custom quote total
  const customTotal = useMemo(() => {
    if (estimateType !== 'custom') return 0;
    const subtotal = lineItems.reduce((sum, item) => {
      if (!item.optional) {
        return sum + (item.qty * item.unitPrice);
      }
      return sum;
    }, 0);
    
    let discountAmount = 0;
    if (discount.type === 'percent' && discount.value > 0) {
      discountAmount = subtotal * (discount.value / 100);
    } else if (discount.type === 'fixed' && discount.value > 0) {
      discountAmount = discount.value;
    }
    
    return subtotal - discountAmount;
  }, [estimateType, lineItems, discount]);

  // Toggle frequency selection
  const toggleFrequency = (freq) => {
    setSelectedFrequencies(prev => {
      if (prev.includes(freq)) {
        if (prev.length === 1) return prev; // Must have at least one
        return prev.filter(f => f !== freq);
      }
      return [...prev, freq];
    });
  };

  // Toggle addon
  const toggleAddon = (addonId) => {
    setSelectedAddons(prev => 
      prev.includes(addonId) 
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  // Line item functions
  const addLineItem = () => {
    setLineItems(prev => [...prev, {
      id: Date.now(),
      description: '',
      qty: 1,
      unitPrice: 0,
      optional: false
    }]);
  };

  const updateLineItem = (id, field, value) => {
    setLineItems(prev => prev.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removeLineItem = (id) => {
    if (lineItems.length === 1) return;
    setLineItems(prev => prev.filter(item => item.id !== id));
  };

  // Create new client
  const handleCreateClient = async (e) => {
    e.preventDefault();
    const client = await addClient({ ...newClient, status: 'lead' });
    setSelectedClientId(client.id);
    setShowNewClientModal(false);
    setNewClient({ firstName: '', lastName: '', email: '', phone: '' });
  };

  // Create new property
  const handleCreateProperty = async (e) => {
    e.preventDefault();
    const property = await addProperty({
      ...newProperty,
      clientId: selectedClientId,
      sqft: newProperty.sqft ? parseInt(newProperty.sqft) : null,
      bedrooms: newProperty.bedrooms ? parseInt(newProperty.bedrooms) : null,
      bathrooms: newProperty.bathrooms ? parseFloat(newProperty.bathrooms) : null,
      isPrimary: clientProps.length === 0
    });
    setSelectedPropertyId(property.id);
    setShowNewPropertyModal(false);
    setNewProperty({
      addressLine1: '', addressLine2: '', city: 'Charlottesville', state: 'VA', zip: '',
      sqft: '', bedrooms: '', bathrooms: ''
    });
  };

  // Save estimate
  const handleSave = async (asDraft = true) => {
    if (!selectedClientId) {
      alert('Please select a client');
      return;
    }

    setIsSaving(true);

    try {
      let estimateData = {
        clientId: selectedClientId,
        propertyId: selectedPropertyId || null,
        status: 'draft', // Always start as draft
        notes: clientNotes,
        internalNotes,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      if (estimateType === 'residential') {
        // Build residential estimate data
        const deepCleanBase = primaryEstimate?.deepCleanEstimate?.subtotal || 0;
        const deepCleanTotal = skipDeepClean ? 0 : (deepCleanBase + addonsTotal);
        
        // Build frequency options for client portal
        const freqOptions = selectedFrequencies
          .filter(freq => estimates[freq])
          .map(freq => ({
            value: freq,
            label: cleaningFrequencies[freq]?.label || freq,
            price: Math.round((estimates[freq]?.total || 0) * 100) / 100
          }));

        // Build deep clean line items
        const dcLineItems = primaryEstimate?.deepCleanEstimate?.lineItems || [];
        const addonLineItems = selectedAddons.map(addonId => {
          const addon = deepCleanAddonsList.find(a => a.id === addonId);
          return {
            id: addonId,
            label: addon?.label || addonId,
            price: addon?.price || 0,
            type: 'addon'
          };
        });

        estimateData = {
          ...estimateData,
          source: 'residential_calculator',
          serviceType: 'residential',
          calculatorInputs: {
            ...calcInputs,
            sqft: parseInt(calcInputs.sqft),
            bedrooms: parseInt(calcInputs.bedrooms),
            bathrooms: parseFloat(calcInputs.bathrooms),
            frequency: primaryFreq
          },
          selectedFrequencies,
          frequencyOptions: freqOptions,
          lineItems: primaryEstimate?.lineItems || [],
          deepCleanLineItems: skipDeepClean ? [] : [...dcLineItems, ...addonLineItems],
          recurringTotal: Math.round((primaryEstimate?.total || 0) * 100) / 100,
          deepCleanTotal: Math.round(deepCleanTotal * 100) / 100,
          total: Math.round((primaryEstimate?.total || 0) * 100) / 100,
          skipDeepClean
        };
      } else {
        // Custom quote
        const validItems = lineItems.filter(item => item.description && item.unitPrice > 0);
        
        estimateData = {
          ...estimateData,
          source: 'custom_estimate_builder',
          serviceType: 'custom',
          lineItems: validItems.map(item => ({
            description: item.description,
            qty: item.qty,
            unitPrice: item.unitPrice,
            total: item.qty * item.unitPrice,
            required: !item.optional
          })),
          discountType: discount.type,
          discountValue: discount.value,
          discountReason: discount.reason,
          total: customTotal
        };
      }

      const newEstimate = await createEstimate(estimateData);
      navigate(`/admin/estimates/${newEstimate.id}`);
    } catch (error) {
      console.error('Error saving estimate:', error);
      alert('Failed to save estimate. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Check if can calculate
  const canCalculate = calcInputs.sqft && calcInputs.bedrooms && calcInputs.bathrooms;

  return (
    <div className="estimate-builder">
      {/* Header */}
      <div className="builder-header">
        <button className="back-btn" onClick={() => navigate('/admin/estimates')}>
          <ArrowLeft size={20} />
        </button>
        <div className="header-title">
          <h1>Create Estimate</h1>
          <p>Build a new estimate for a client</p>
        </div>
      </div>

      {/* Estimate Type Selector */}
      <div className="type-selector">
        <div 
          className={`type-card residential ${estimateType === 'residential' ? 'selected' : ''}`}
          onClick={() => setEstimateType('residential')}
        >
          <div className="type-icon">
            <Home size={24} />
          </div>
          <h3>Residential Cleaning</h3>
          <p>Standard recurring cleaning service with calculator</p>
          {estimateType === 'residential' && <span className="type-badge">Selected</span>}
        </div>
        
        <div 
          className={`type-card custom ${estimateType === 'custom' ? 'selected' : ''}`}
          onClick={() => setEstimateType('custom')}
        >
          <div className="type-icon">
            <FileText size={24} />
          </div>
          <h3>Custom Quote</h3>
          <p>Build a custom quote with line items</p>
          {estimateType === 'custom' && <span className="type-badge">Selected</span>}
        </div>
      </div>

      {/* Main Layout */}
      <div className="builder-layout">
        {/* Left Column - Form */}
        <div className="builder-main">
          {/* Client Selection */}
          <div className="builder-card">
            <div className="card-header">
              <h2><User size={18} /> Client</h2>
              <button className="btn btn-sm btn-secondary" onClick={() => setShowNewClientModal(true)}>
                <Plus size={14} /> New
              </button>
            </div>
            <div className="card-body">
              <div className="client-selector">
                <div className="search-input">
                  <Search size={16} />
                  <input
                    type="text"
                    placeholder="Search clients..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                  />
                </div>
              </div>
              
              {selectedClient ? (
                <div className="selected-client">
                  <div className="client-avatar">
                    {selectedClient.firstName?.[0]}{selectedClient.lastName?.[0]}
                  </div>
                  <div className="client-info">
                    <span className="client-name">{formatFullName(selectedClient)}</span>
                    <span className="client-meta">
                      {selectedClient.phone && <span>{selectedClient.phone}</span>}
                      {selectedClient.email && <span>{selectedClient.email}</span>}
                    </span>
                  </div>
                  <button className="btn-clear" onClick={() => { setSelectedClientId(''); setSelectedPropertyId(''); }}>
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="client-list">
                  {filteredClients.map(client => (
                    <button 
                      key={client.id} 
                      className="client-option"
                      onClick={() => setSelectedClientId(client.id)}
                    >
                      <div className="client-avatar sm">
                        {client.firstName?.[0]}{client.lastName?.[0]}
                      </div>
                      <div className="client-info">
                        <span className="client-name">{formatFullName(client)}</span>
                        <span className="client-meta">
                          {client.phone && <span>{client.phone}</span>}
                          {client.email && <span>{client.email}</span>}
                        </span>
                      </div>
                      <ChevronRight size={16} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Property Selection */}
          {selectedClientId && (
            <div className="builder-card">
              <div className="card-header">
                <h2><MapPin size={18} /> Property</h2>
                <button className="btn btn-sm btn-secondary" onClick={() => setShowNewPropertyModal(true)}>
                  <Plus size={14} /> Add
                </button>
              </div>
              <div className="card-body">
                {clientProps.length > 0 ? (
                  <div className="property-list">
                    {clientProps.map(prop => (
                      <button
                        key={prop.id}
                        className={`property-option ${selectedPropertyId === prop.id ? 'selected' : ''}`}
                        onClick={() => setSelectedPropertyId(prop.id)}
                      >
                        <div className="property-icon">
                          <MapPin size={18} />
                        </div>
                        <div className="property-info">
                          <span className="property-address">{prop.addressLine1}</span>
                          <span className="property-meta">
                            {prop.city}, {prop.state} {prop.zip}
                            {prop.sqft && ` • ${prop.sqft.toLocaleString()} sqft`}
                          </span>
                        </div>
                        {selectedPropertyId === prop.id && <Check size={18} className="check-icon" />}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state-sm">
                    <p>No properties for this client</p>
                    <button className="btn btn-primary btn-sm" onClick={() => setShowNewPropertyModal(true)}>
                      <Plus size={14} /> Add Property
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Residential Calculator */}
          {estimateType === 'residential' && (
            <>
              {/* Property Details */}
              <div className="builder-card">
                <div className="card-header">
                  <h2><Home size={18} /> Property Details</h2>
                </div>
                <div className="card-body">
                  <div className="form-row three-col">
                    <div className="form-group">
                      <label>Square Feet <span className="required">*</span></label>
                      <div className="input-with-unit">
                        <input
                          type="number"
                          value={calcInputs.sqft}
                          onChange={(e) => setCalcInputs(prev => ({ ...prev, sqft: e.target.value }))}
                          placeholder="2200"
                        />
                        <span className="unit">sqft</span>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Bedrooms <span className="required">*</span></label>
                      <input
                        type="number"
                        value={calcInputs.bedrooms}
                        onChange={(e) => setCalcInputs(prev => ({ ...prev, bedrooms: e.target.value }))}
                        placeholder="3"
                      />
                    </div>
                    <div className="form-group">
                      <label>Bathrooms <span className="required">*</span></label>
                      <input
                        type="number"
                        step="0.5"
                        value={calcInputs.bathrooms}
                        onChange={(e) => setCalcInputs(prev => ({ ...prev, bathrooms: e.target.value }))}
                        placeholder="2.5"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Home Condition</label>
                    <div className="condition-grid">
                      {conditionOptions.map(opt => (
                        <div
                          key={opt.value}
                          className={`condition-option ${calcInputs.condition === opt.value ? 'selected' : ''}`}
                          onClick={() => setCalcInputs(prev => ({ ...prev, condition: opt.value }))}
                        >
                          <span className="condition-emoji">{opt.emoji}</span>
                          <span className="condition-label">{opt.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={calcInputs.hasPets}
                          onChange={(e) => setCalcInputs(prev => ({ ...prev, hasPets: e.target.checked }))}
                        />
                        <span>Has Pets</span>
                      </label>
                    </div>
                    {calcInputs.hasPets && (
                      <div className="form-group">
                        <label>Number of Pets</label>
                        <input
                          type="number"
                          min="1"
                          value={calcInputs.petCount}
                          onChange={(e) => setCalcInputs(prev => ({ ...prev, petCount: parseInt(e.target.value) || 1 }))}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Frequency Selection */}
              <div className="builder-card">
                <div className="card-header">
                  <h2><Repeat size={18} /> Service Frequency</h2>
                  <span className="header-hint">Select frequencies to offer</span>
                </div>
                <div className="card-body">
                  <div className="frequency-grid">
                    {frequencyOptions.map(opt => {
                      const est = estimates[opt.value];
                      const price = est?.total || 0;
                      return (
                        <div
                          key={opt.value}
                          className={`frequency-option ${selectedFrequencies.includes(opt.value) ? 'selected' : ''}`}
                          onClick={() => toggleFrequency(opt.value)}
                        >
                          <span className="freq-name">{opt.label}</span>
                          <span className="freq-price">
                            {canCalculate ? formatCurrency(price) : '—'}
                          </span>
                          {opt.multiplier !== 1.0 && (
                            <span className="freq-multiplier">{Math.round((opt.multiplier - 1) * 100)}% adj</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Deep Clean Add-ons */}
              <div className="builder-card">
                <div className="card-header">
                  <h2><Sparkles size={18} /> Deep Clean Options</h2>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={skipDeepClean}
                      onChange={(e) => setSkipDeepClean(e.target.checked)}
                    />
                    <span>Skip Deep Clean</span>
                  </label>
                </div>
                {!skipDeepClean && (
                  <div className="card-body">
                    <p className="section-note">Add-ons for the initial deep clean visit:</p>
                    <div className="addons-grid">
                      {deepCleanAddonsList.map(addon => (
                        <div
                          key={addon.id}
                          className={`addon-option ${selectedAddons.includes(addon.id) ? 'selected' : ''}`}
                          onClick={() => toggleAddon(addon.id)}
                        >
                          <div className="addon-checkbox">
                            {selectedAddons.includes(addon.id) && <Check size={14} />}
                          </div>
                          <div className="addon-info">
                            <span className="addon-name">{addon.label}</span>
                          </div>
                          <span className="addon-price">+{formatCurrency(addon.price)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Custom Quote Line Items */}
          {estimateType === 'custom' && (
            <div className="builder-card">
              <div className="card-header">
                <h2><FileText size={18} /> Line Items</h2>
              </div>
              <div className="card-body">
                <div className="line-items-header">
                  <span>Description</span>
                  <span>Qty</span>
                  <span>Price</span>
                  <span>Total</span>
                  <span></span>
                </div>
                <div className="line-items-container">
                  {lineItems.map((item, index) => (
                    <div key={item.id} className="line-item-row">
                      <div className="line-item-desc-cell">
                        <input
                          type="text"
                          placeholder="Service description..."
                          value={item.description}
                          onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                        />
                        <label className="line-item-optional-toggle">
                          <div className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={item.optional}
                              onChange={(e) => updateLineItem(item.id, 'optional', e.target.checked)}
                            />
                            <span className="toggle-slider"></span>
                          </div>
                          <span className="toggle-label">Optional</span>
                        </label>
                      </div>
                      <input
                        type="number"
                        min="1"
                        value={item.qty}
                        onChange={(e) => updateLineItem(item.id, 'qty', parseInt(e.target.value) || 1)}
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unitPrice}
                        onChange={(e) => updateLineItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                        placeholder="0.00"
                      />
                      <span className="line-item-total">
                        {formatCurrency(item.qty * item.unitPrice)}
                      </span>
                      <button 
                        className="remove-line-btn"
                        onClick={() => removeLineItem(item.id)}
                        disabled={lineItems.length === 1}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
                <button className="add-line-btn" onClick={addLineItem}>
                  <Plus size={16} /> Add Line Item
                </button>

                {/* Discount Section */}
                <div className="discount-section">
                  <div className="discount-header">
                    <span>Discount</span>
                  </div>
                  <div className="discount-inputs">
                    <select 
                      value={discount.type}
                      onChange={(e) => setDiscount(prev => ({ ...prev, type: e.target.value }))}
                    >
                      <option value="percent">%</option>
                      <option value="fixed">$</option>
                    </select>
                    <input
                      type="number"
                      min="0"
                      value={discount.value}
                      onChange={(e) => setDiscount(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                      placeholder="0"
                    />
                    <input
                      type="text"
                      placeholder="Reason (optional)"
                      value={discount.reason}
                      onChange={(e) => setDiscount(prev => ({ ...prev, reason: e.target.value }))}
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="builder-card">
            <div className="card-header">
              <h2><FileText size={18} /> Notes</h2>
            </div>
            <div className="card-body">
              <div className="notes-tabs">
                <button 
                  className={`notes-tab ${activeTab === 'client' ? 'active' : ''}`}
                  onClick={() => setActiveTab('client')}
                >
                  Client Notes
                </button>
                <button 
                  className={`notes-tab ${activeTab === 'internal' ? 'active' : ''}`}
                  onClick={() => setActiveTab('internal')}
                >
                  Internal Notes
                </button>
              </div>
              {activeTab === 'client' ? (
                <textarea
                  value={clientNotes}
                  onChange={(e) => setClientNotes(e.target.value)}
                  placeholder="Notes visible to the client on the estimate..."
                  rows={4}
                />
              ) : (
                <textarea
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder="Internal notes for staff only..."
                  rows={4}
                />
              )}
            </div>

          </div>
        </div>

        {/* Right Column - Summary */}
        <div className="builder-sidebar">
          <div className="summary-card">
            <div className="summary-header">
              <h3>Estimate Summary</h3>
              <p>{estimateType === 'residential' ? 'Residential Cleaning' : 'Custom Quote'}</p>
            </div>
            
            <div className="summary-body">
              {/* Client Info */}
              {selectedClient && (
                <div className="summary-section">
                  <div className="summary-label">Client</div>
                  <div className="summary-value">{formatFullName(selectedClient)}</div>
                  {selectedProperty && (
                    <div className="summary-value-sm">{selectedProperty.addressLine1}</div>
                  )}
                </div>
              )}

              {/* Residential Summary */}
              {estimateType === 'residential' && canCalculate && (
                <>
                  {!skipDeepClean && primaryEstimate?.deepCleanEstimate && (
                    <div className="summary-deep-clean">
                      <div className="dc-label">
                        <Sparkles size={14} />
                        <span>Deep Clean</span>
                      </div>
                      <div className="dc-price">
                        {formatCurrency((primaryEstimate.deepCleanEstimate.subtotal || 0) + addonsTotal)}
                      </div>
                      {addonsTotal > 0 && (
                        <div className="dc-addons">
                          Includes {formatCurrency(addonsTotal)} in add-ons
                        </div>
                      )}
                    </div>
                  )}

                  <div className="summary-section">
                    <div className="summary-label">Recurring Service</div>
                    {selectedFrequencies.map(freq => {
                      const est = estimates[freq];
                      return (
                        <div key={freq} className="summary-row">
                          <span>{cleaningFrequencies[freq]?.label || freq}</span>
                          <span>{formatCurrency(est?.total || 0)}</span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="summary-total">
                    <span>Per Visit</span>
                    <span>{formatCurrency(primaryEstimate?.total || 0)}</span>
                  </div>
                </>
              )}

              {/* Custom Quote Summary */}
              {estimateType === 'custom' && (
                <>
                  <div className="summary-section">
                    <div className="summary-label">Line Items</div>
                    {lineItems.filter(i => i.description).map(item => (
                      <div key={item.id} className={`summary-row ${item.optional ? 'optional' : ''}`}>
                        <span>{item.description} {item.optional && '(opt)'}</span>
                        <span>{formatCurrency(item.qty * item.unitPrice)}</span>
                      </div>
                    ))}
                  </div>

                  {discount.value > 0 && (
                    <div className="summary-row discount">
                      <span>Discount</span>
                      <span>
                        -{discount.type === 'percent' ? `${discount.value}%` : formatCurrency(discount.value)}
                      </span>
                    </div>
                  )}

                  <div className="summary-total">
                    <span>Total</span>
                    <span>{formatCurrency(customTotal)}</span>
                  </div>
                </>
              )}

              {/* Empty state */}
              {estimateType === 'residential' && !canCalculate && (
                <div className="summary-empty">
                  <Calculator size={32} />
                  <p>Enter property details to calculate estimate</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Action Footer */}
      <div className="action-footer">
        <div className="action-footer-content">
          <div className="action-footer-summary">
            {canCalculate && primaryEstimate && (
              <>
                <span className="footer-label">Per Visit:</span>
                <span className="footer-price">{formatCurrency(primaryEstimate?.total || 0)}</span>
              </>
            )}
          </div>
          <div className="action-footer-buttons">
            <button 
              className="btn btn-secondary"
              onClick={() => handleSave(true)}
              disabled={isSaving || !selectedClientId}
            >
              Save as Draft
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => handleSave(false)}
              disabled={isSaving || !selectedClientId}
            >
              {isSaving ? <Loader size={18} className="spinning" /> : <Check size={18} />}
              Save & Preview
            </button>
          </div>
        </div>
      </div>

      {/* New Client Modal */}
      <Modal isOpen={showNewClientModal} onClose={() => setShowNewClientModal(false)} title="New Client">
        <form onSubmit={handleCreateClient} className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                value={newClient.firstName}
                onChange={(e) => setNewClient(prev => ({ ...prev, firstName: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input
                type="text"
                value={newClient.lastName}
                onChange={(e) => setNewClient(prev => ({ ...prev, lastName: e.target.value }))}
                required
              />
            </div>
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={newClient.email}
              onChange={(e) => setNewClient(prev => ({ ...prev, email: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label>Phone *</label>
            <input
              type="tel"
              value={newClient.phone}
              onChange={(e) => setNewClient(prev => ({ ...prev, phone: e.target.value }))}
              required
            />
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowNewClientModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">Create Client</button>
          </div>
        </form>
      </Modal>

      {/* New Property Modal */}
      <Modal isOpen={showNewPropertyModal} onClose={() => setShowNewPropertyModal(false)} title="Add Property">
        <form onSubmit={handleCreateProperty} className="modal-form">
          <div className="form-group">
            <label>Street Address *</label>
            <input
              type="text"
              value={newProperty.addressLine1}
              onChange={(e) => setNewProperty(prev => ({ ...prev, addressLine1: e.target.value }))}
              required
            />
          </div>
          <div className="form-group">
            <label>Apt/Suite</label>
            <input
              type="text"
              value={newProperty.addressLine2}
              onChange={(e) => setNewProperty(prev => ({ ...prev, addressLine2: e.target.value }))}
            />
          </div>
          <div className="form-row three-col">
            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                value={newProperty.city}
                onChange={(e) => setNewProperty(prev => ({ ...prev, city: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label>State</label>
              <input
                type="text"
                value={newProperty.state}
                onChange={(e) => setNewProperty(prev => ({ ...prev, state: e.target.value }))}
                maxLength={2}
              />
            </div>
            <div className="form-group">
              <label>ZIP *</label>
              <input
                type="text"
                value={newProperty.zip}
                onChange={(e) => setNewProperty(prev => ({ ...prev, zip: e.target.value }))}
                required
              />
            </div>
          </div>
          <div className="form-row three-col">
            <div className="form-group">
              <label>Square Feet</label>
              <input
                type="number"
                value={newProperty.sqft}
                onChange={(e) => setNewProperty(prev => ({ ...prev, sqft: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Bedrooms</label>
              <input
                type="number"
                value={newProperty.bedrooms}
                onChange={(e) => setNewProperty(prev => ({ ...prev, bedrooms: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Bathrooms</label>
              <input
                type="number"
                step="0.5"
                value={newProperty.bathrooms}
                onChange={(e) => setNewProperty(prev => ({ ...prev, bathrooms: e.target.value }))}
              />
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowNewPropertyModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">Add Property</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default EstimateNew;
